import React, { useCallback, useEffect, useState } from 'react';
import { Box } from '@mui/material';

import { Container, Loading, SimpleButton, ErrorMessage, SimpleTextField } from '@/src/ui';
import { apiRequest } from '@/src/services';
import { useAuth } from '@/src/app/context/useAuth';
import { Constants } from '@/src/utils';
import type { ClassDTO } from './models/classDTOs.ts';
import ClassesTable from './components/ClassesTable.tsx';
import ClassRosterTable from './components/ClassRosterTable';
import ClassFilters from './components/ClassFilters.tsx';
import DeleteClassModal from './components/DeleteClassModal.tsx';
import CreateEditClassModal from './components/CreateEditClassModal.tsx';

const ClassesPage: React.FC = () => {
    document.title = 'Classes';

    const { user } = useAuth();
    const [loaded, setLoaded] = useState(true);
    const [error, setError] = useState('');
    const [hasSearched, setHasSearched] = useState(false);

    const [classes, setClasses] = useState<ClassDTO[]>([]);
    const [filteredClasses, setFilteredClasses] = useState<ClassDTO[]>([]);
    const [selectedClassId, setSelectedClassId] = useState<number | null>(null);
    const [classDetails, setClassDetails] = useState<any>(null);

    const userId = user?.id || 'nouser';
    const FILTER_KEY = `classes-filters-${userId}`;
    const [filterState, setFilterState] = useState(() => {
        try {
            const saved = localStorage.getItem(FILTER_KEY);
            if (saved) return JSON.parse(saved);
        } catch {
            // intentionally empty
        }
        return {
            districtId: 0,
            schoolId: 0,
            gradeLevelMasterId: 0,
        };
    });
    useEffect(() => {
        if (!userId) return;
        try {
            localStorage.setItem(FILTER_KEY, JSON.stringify(filterState));
        } catch {
            // intentionally empty
        }
    }, [filterState, userId, FILTER_KEY]);

    useEffect(
        () => {
            if (user?.role === Constants.ROLES.TEACHER) {
                refreshClasses();
            }
        }, //eslint-disable-next-line react-hooks/exhaustive-deps
        [user?.role]
    );

    const [searchString, setSearchString] = useState<string>('');

    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [selectedClass, setSelectedClass] = useState<ClassDTO | null>(null);

    const refreshClasses = useCallback(async () => {
        setError('');
        setLoaded(false);
        setHasSearched(true);
        try {
            let url = 'dbo/classes';
            const params = [];
            if (filterState.districtId) params.push(`districtId=${filterState.districtId}`);
            if (filterState.schoolId) params.push(`schoolId=${filterState.schoolId}`);
            if (filterState.gradeLevelMasterId)
                params.push(`gradeLevelId=${filterState.gradeLevelMasterId}`);
            if (params.length) url += '?' + params.join('&');
            const res = await apiRequest<ClassDTO[]>('GET', url);
            setClasses(res.data ?? []);
        } catch {
            setError('Failed to fetch classes');
        } finally {
            setLoaded(true);
        }
    }, [filterState.districtId, filterState.schoolId, filterState.gradeLevelMasterId]);

    useEffect(() => {
        setFilteredClasses(
            classes.filter((c) => c.className.toLowerCase().includes(searchString.toLowerCase()))
        );
    }, [classes, searchString]);

    const handleSearch = (district: number, school: number, grade: number) => {
        setFilterState({
            districtId: district,
            schoolId: school,
            gradeLevelMasterId: grade,
        });
        setTimeout(() => {
            refreshClasses();
        }, 0);
    };

    useEffect(() => {
        async function fetchClassDetails() {
            if (selectedClassId) {
                try {
                    const res = await apiRequest<any>('GET', `dbo/classes/${selectedClassId}`);
                    setClassDetails(res.data);
                } catch {
                    setClassDetails(null);
                }
            } else {
                setClassDetails(null);
            }
        }
        fetchClassDetails();
    }, [selectedClassId]);

    const onSubmitCallback = () => {
        setShowCreateModal(false);
        setShowDeleteModal(false);
        setSelectedClass(null);
        refreshClasses();
    };

    return (
        <Container>
            <h1 className="page-header">Classes</h1>
            <br />

            <ClassFilters
                filterState={filterState}
                setFilterState={setFilterState}
                onSearch={handleSearch}
            />
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    mb: 2,
                }}
            >
                <SimpleTextField
                    name="searchClasses"
                    label="Search by name..."
                    variant="outlined"
                    value={searchString}
                    onChange={(e) => setSearchString(e.target.value)}
                    sx={{ minWidth: 170, maxWidth: 300, mr: 2 }}
                />
                {user?.role === Constants.ROLES.SUPER_ADMIN && (
                    <SimpleButton
                        onClick={() => setShowCreateModal(true)}
                        variant="contained"
                        color="primary"
                        className="whitespace-nowrap"
                    >
                        Add Class
                    </SimpleButton>
                )}
            </Box>
            <Loading visible={!loaded} message="Loading classes..." />
            <ErrorMessage error={error} />
            {loaded && (
                <ClassesTable
                    data={filteredClasses}
                    onEdit={(row) => {
                        setSelectedClass(row);
                        setShowCreateModal(true);
                    }}
                    onDelete={(row) => {
                        setSelectedClass(row);
                        setShowDeleteModal(true);
                    }}
                    emptyMessage={hasSearched ? 'No classes found.' : ''}
                    onShowRoster={(row) => setSelectedClassId(row.classId)}
                />
            )}

            {selectedClassId && classDetails && <ClassRosterTable classDetails={classDetails} />}
            {showDeleteModal && selectedClass && (
                <DeleteClassModal
                    open={showDeleteModal}
                    classItem={selectedClass}
                    onClose={onSubmitCallback}
                    setError={setError}
                    onSubmitCallback={onSubmitCallback}
                    error={error}
                />
            )}
            {showCreateModal && (
                <CreateEditClassModal
                    open={showCreateModal}
                    onClose={() => {
                        setShowCreateModal(false);
                        setSelectedClass(null);
                    }}
                    setError={setError}
                    onSubmitCallback={onSubmitCallback}
                    districtId={filterState.districtId}
                    schoolId={filterState.schoolId}
                    classToEdit={selectedClass}
                />
            )}
        </Container>
    );
};

export default ClassesPage;
